import { trackCreativeAction } from 'analytics';
import forge from 'apis/forge';
import SidebarToggle from 'components/Widget/Button/SidebarToggle';
import LoadingOverlay from 'components/Widget/Overlay/LoadingOverlay';
import { CreateDesignSystemFromPromptResponse } from 'dragonmouse-shared/api/forge';
import { Attachment } from 'dragonmouse-shared/interfaces';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useNavigate } from 'react-router';
import { shallowEqual, useSelector } from 'react-redux';
import { selectScenes, selectIsFreeTier } from 'redux/db/reducers';
import { RootReducer } from 'redux/reducers';
import { displayUpgradeModal } from 'redux/mode/util';
import * as routes from 'routes';
import CreatorInput from '../CreatorInput';
import SceneLinkToggle from './SceneLinkToggle';
import * as Base from '../styles';

const DesignSystemCreator = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const sceneId = searchParams.get('sceneId');
	const [loadingMessage, setLoadingMessage] = useState<string>('');
	const [linkToScene, setLinkToScene] = useState<boolean>(true);

	const scenes = useSelector((r: RootReducer) => selectScenes(r.db), shallowEqual);
	const isFreeTier = useSelector((r: RootReducer) => selectIsFreeTier(r.db));
	const scene = sceneId ? scenes.find((s) => s.id === sceneId) : null;

	// Default linkToScene to true if sceneId is present
	useEffect(() => {
		if (sceneId) {
			setLinkToScene(true);
		}
	}, [sceneId]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const onNewDesignSystem = async (prompt: string, attachments: Attachment[]) => {
		if (isFreeTier) {
			displayUpgradeModal('Creating design systems');
			return;
		}

		try {
			setLoadingMessage('creating design system...');
			trackCreativeAction('new_design_system');
			trackCreativeAction('new_ds_chat');
			const res: CreateDesignSystemFromPromptResponse =
				await forge.createDesignSystemFromPrompt.withToast()({
					prompt,
					attachments,
					sceneId: linkToScene && sceneId ? sceneId : undefined,
				});
			setLoadingMessage('');
			navigate(routes.designSystem(res.designSystem.id, res.designSystem.title));
		} catch (e) {
			setLoadingMessage('');
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	return (
		<Base.Base>
			<Base.SidebarToggle>
				<SidebarToggle />
			</Base.SidebarToggle>
			{loadingMessage && <LoadingOverlay text={loadingMessage} semiTransparent />}
			<Base.Content>
				<Base.Input>
					<Base.Title>Create a design system</Base.Title>
					<CreatorInput
						onSubmit={onNewDesignSystem}
						disabled={!!loadingMessage}
						mode="designSystem"
					/>
					{sceneId && scene && (
						<SceneLinkToggle
							sceneName={scene.title}
							checked={linkToScene}
							onChange={setLinkToScene}
						/>
					)}
				</Base.Input>
			</Base.Content>
		</Base.Base>
	);
};

export default DesignSystemCreator;
